import { useEffect, useRef, useMemo } from 'react';

import { useSearchParams, useLocation } from 'react-router-dom';

import { Staff } from '../../../types';
import { StaffFilterSelection } from '../../../components/StaffFilter/types';
import { getStaffById } from '../../../api';

const STAFF_URL_PARAM = 'staff';
const LEGACY_STAFF_URL_PARAM = 'staffFilter'; // Old param name to clean up

// URL value for "everyone" selection (current_user is the default, so no URL param needed)
const URL_VALUE_EVERYONE = 'everyone';

// Pages where the staff filter URL param should be synced
const STAFF_FILTER_PAGES = ['/customers', '/orders', '/activities'];

type UseStaffUrlSyncParams = {
  selection: StaffFilterSelection;
  setSelection: (selection: StaffFilterSelection) => void;
  loadInitial: () => Promise<Staff[]>;
  setSelectedStaff: (staff: Staff | null) => void;
};

/**
 * Hook for syncing staff selection with URL parameters.
 * Handles both directions:
 * 1. URL → State: Restore selection from URL on mount
 * 2. State → URL: Sync selection changes back to URL
 *
 * URL patterns:
 * - (no param)       -> Current user (default)
 * - ?staff=everyone  -> Everyone (no filter)
 * - ?staff=123       -> Specific staff ID
 */
export const useStaffUrlSync = ({
  selection,
  setSelection,
  loadInitial,
  setSelectedStaff,
}: UseStaffUrlSyncParams): void => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const isInitializedRef = useRef(false);
  // Track when URL restoration is complete (prevents State → URL sync from running too early)
  const urlRestorationCompleteRef = useRef(false);

  // Capture initial URL staff filter value once on mount
  const initialUrlValue = useMemo(() => {
    return searchParams.get(STAFF_URL_PARAM);
  }, []);

  // URL → State: Initialize and restore selection from URL
  useEffect(() => {
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    const initialize = async() => {
      const results = await loadInitial();

      // No URL param means use default (current_user) - don't override
      if (!initialUrlValue) {
        urlRestorationCompleteRef.current = true;
        return;
      }

      if (initialUrlValue === URL_VALUE_EVERYONE) {
        setSelection({ type: 'everyone' });
      } else {
        // Try to parse as staff ID
        const staffId = parseInt(initialUrlValue, 10);
        let resolved = false;

        if (!isNaN(staffId)) {
          let staff = results.find((s) => s.id === staffId);

          // If not in initial results, fetch by ID
          if (!staff) {
            try {
              staff = await getStaffById(staffId);
            } catch {
              // Staff not found, will fall back to everyone
            }
          }

          if (staff) {
            setSelection({ type: 'specific_staff', staff });
            setSelectedStaff(staff);
            resolved = true;
          }
        }

        // If staff ID is invalid or not found, fall back to everyone
        // This also clears the invalid URL param via State → URL sync
        if (!resolved) {
          setSelection({ type: 'everyone' });
        }
      }

      // Mark URL restoration as complete AFTER setting selection
      urlRestorationCompleteRef.current = true;
    };

    initialize();
  }, [initialUrlValue, loadInitial, setSelection, setSelectedStaff]);

  // State → URL: Sync selection changes back to URL
  useEffect(() => {
    // Don't sync to URL until URL restoration is complete
    if (!urlRestorationCompleteRef.current) return;

    const isStaffFilterPage = STAFF_FILTER_PAGES.some((page) => location.pathname.startsWith(page));
    if (!isStaffFilterPage) return;

    const currentUrlValue = searchParams.get(STAFF_URL_PARAM);
    const hasLegacyParam = searchParams.has(LEGACY_STAFF_URL_PARAM);

    // Map selection type to URL value
    // current_user is the default, so no URL param needed
    let targetUrlValue: string | null;
    switch (selection.type) {
      case 'everyone':
        targetUrlValue = URL_VALUE_EVERYONE;
        break;
      case 'current_user':
        targetUrlValue = null; // Default - no param needed
        break;
      case 'specific_staff':
        targetUrlValue = selection.staff.id.toString();
        break;
    }

    if (currentUrlValue !== targetUrlValue || hasLegacyParam) {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (targetUrlValue) {
            params.set(STAFF_URL_PARAM, targetUrlValue);
          } else {
            params.delete(STAFF_URL_PARAM);
          }
          params.delete(LEGACY_STAFF_URL_PARAM); // Clean up old param
          return params;
        },
        { replace: true },
      );
    }
  }, [selection, searchParams, setSearchParams, location.pathname]);
};
